import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  IconButton,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../reusableComponent/Reusables";
import cylinderMasterService from "../../../services/masters/cylinderMaster.Service";
import depotMasterService from "../../../services/masters/depotMaster.Service";
import plantMasterService from "../../../services/masters/plantMaster.Service";
import customerMasterService from "../../../services/masters/customerMaster.Service";
import productMasterService from "../../../services/masters/productMaster.Service";

// ── Styles (consistent with rest of app) ─────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Inter', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px", color: "#718096", mb: 2,
  display: "flex", alignItems: "center", gap: "6px",
};

const pageHeaderSx = {
  display: "flex", alignItems: "center",
  justifyContent: "space-between", mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px", fontWeight: 700, color: "#000",
  display: "flex", alignItems: "center", gap: "10px",
};

const backBtnSx = {
  display: "flex", alignItems: "center", gap: "6px",
  fontSize: "13px", fontWeight: 600, color: "#2B6CB0",
  padding: "7px 14px", border: "1.5px solid #2B6CB0",
  borderRadius: "7px", background: "#fff", textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff", borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  border: "1px solid #E2E8F0", overflow: "hidden", mb: 2,
};

const sectionHeaderSx = {
  background: "#1C3A5E", padding: "12px 22px",
  display: "flex", alignItems: "center", gap: "10px",
};

const sectionHeaderTitleSx = {
  fontSize: "13px", fontWeight: 600, color: "#fff", letterSpacing: "0.3px",
};

const formBodySx = { padding: "20px 22px" };

const fieldLabelSx = {
  fontSize: "12px", fontWeight: 600, color: "#2063b6",
  mb: "4px", display: "flex", alignItems: "center", gap: "4px",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    background: "#fafafa", height: "36px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
  },
  "& input": {
    color: "#1A202C", fontSize: "13px",
    fontFamily: "'Inter', Arial, sans-serif",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
};

const addBtnSx = {
  background: "#2B6CB0", color: "#fff", textTransform: "none",
  borderRadius: "8px", fontSize: "13px", fontWeight: 600,
  height: "38px", px: 2.5,
  "&:hover": { background: "#1a4a8a" },
  display: "flex", alignItems: "center", gap: "6px",
};

const tableHeadCellSx = {
  background: "#1C3A5E !important", color: "#fff !important",
  fontSize: "12px", fontWeight: 600,
  fontFamily: "'Inter', Arial, sans-serif", whiteSpace: "nowrap",
};

const formFooterSx = {
  padding: "18px 24px", background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex", alignItems: "center",
  justifyContent: "flex-end", gap: "10px",
};

const saveBtnSx = {
  display: "flex", alignItems: "center", gap: "7px",
  padding: "0 22px", height: "40px", background: "#4CAF50",
  color: "#fff", border: "none", fontSize: "14px", fontWeight: 600,
  textTransform: "none", borderRadius: "8px",
  "&:hover": { background: "#028b19" },
};

const cancelBtnSx = {
  display: "flex", alignItems: "center", gap: "7px",
  padding: "0 22px", height: "40px", background: "#E53E3E",
  color: "#fff", border: "none", textTransform: "none",
  "&:hover": { background: "#971f16" },
  fontSize: "14px", fontWeight: 600, borderRadius: "8px",
};

const menuItemSx = { fontSize: "13px", fontFamily: "'Inter', Arial, sans-serif" };

// ─────────────────────────────────────────────────────────────────────────

const emptyForm = {
  cylinder_no: "",
  tag_no: "",
  owner: "TGPL",
  current_location: "",   // Depot | Plant | Customer
  location_option: null,  // the selected depot/plant/customer object
  prod_code: "",
  prod_id: "",
  tare_wt: "",
};

const CylinderLocationEntry = () => {
  const navigate = useNavigate();
  const login_details = sessionStorage.getItem("loginUser");
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;

  const [depot, setDepot] = useState([]);
  const [plant, setPlant] = useState([]);
  const [customer, setCustomer] = useState([]);
  const [product, setProduct] = useState([]);
const [existingCylinders, setExistingCylinders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const showSuccess = (msg) => setSnack({ open: true, message: msg, severity: "success" });
  const showError = (msg) => setSnack({ open: true, message: msg, severity: "error" });

 useEffect(() => {
  async function fetchMasters() {
    try {
      const [depotData, plantData, customerData, productData, cylinderData] = await Promise.all([
        depotMasterService.getDepotList({ page: 0, size: 999, searchKey: "" }),
        plantMasterService.getPlantList({ page: 0, size: 999, searchKey: "" }),
        customerMasterService.getCustomerList({ page: 0, size: 999, searchKey: "" }),
        productMasterService.getProductList({ page: 0, size: 999, searchKey: "" }),
        cylinderMasterService.getCylinderList({ page: 0, size: 9999, searchKey: "" }),
      ]);
      setDepot(depotData || []);
      setPlant(plantData || []);
      setCustomer(customerData || []);
      setProduct(productData || []);
      setExistingCylinders(cylinderData || []);
    } catch (error) {
      console.error("Error fetching master data", error);
      showError("Error fetching master data");
    }
  }
  fetchMasters();
}, []);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationTypeChange = (value) => {
    setForm((prev) => ({ ...prev, current_location: value, location_option: null }));
  };

  const getLocationOptions = () => {
    if (form.current_location === "Depot") return depot;
    if (form.current_location === "Plant") return plant;
    if (form.current_location === "Customer") return customer;
    return [];
  };

const getLocationOptionLabel = (option) => {
  if (!option) return "";
  if (form.current_location === "Depot") return option.depot_name || "";
  if (form.current_location === "Plant") return option.plant_name || "";
  if (form.current_location === "Customer") return option.cust_name || "";
  return "";
};

  const handleProductChange = (event, newValue) => {
    setForm((prev) => ({
      ...prev,
      prod_code: newValue ? newValue.prod_code : "",
      prod_id: newValue ? newValue.prod_id : "",
    }));
  };

  const resetForm = () => setForm(emptyForm);

 const handleAddToTable = () => {
  if (!form.cylinder_no) {
    showError("Please enter Cylinder No");
    return;
  }

  // Check against already-saved cylinders in DB
  const alreadyInDb = existingCylinders.some(
    (c) => (c.cylinder_no || "").toLowerCase() === form.cylinder_no.toLowerCase()
  );
  if (alreadyInDb) {
    showError(`Cylinder No "${form.cylinder_no}" already exists in the system`);
    return;
  }

  // Check against cylinders already added to this table (not yet saved)
  const alreadyInTable = rows.some(
    (r) => r.cylinder_no.toLowerCase() === form.cylinder_no.toLowerCase()
  );
  if (alreadyInTable) {
    showError(`Cylinder No "${form.cylinder_no}" is already added to the list`);
    return;
  }

  if (!form.tag_no) {
    showError("Please enter Tag No");
    return;
  }
    if (!form.current_location || !form.location_option) {
      showError("Please select Current Location");
      return;
    }

    const locationLabel = getLocationOptionLabel(form.location_option);

    const newRow = {
      sr_no: rows.length + 1,
      cylinder_no: form.cylinder_no,
      tag_no: form.tag_no,
      owner: form.owner,
      current_location: form.current_location,
      location_id:
        form.current_location === "Depot" ? form.location_option.depot_id :
        form.current_location === "Plant" ? form.location_option.plant_id :
        form.current_location === "Customer" ? form.location_option.cust_id : "",
      location_label: locationLabel,
      prod_code: form.prod_code,
      prod_id: form.prod_id,
      tare_wt: form.tare_wt,
    };

    setRows((prev) => [...prev, newRow]);
    resetForm();
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, sr_no: i + 1 })));
  };

const buildCylinderPayload = (row) => {
  const cylinderData = {
    cylinder_no: row.cylinder_no,
    tag_no: row.tag_no,
    owner: (row.owner || "").toUpperCase(),
    prod_id: row.prod_id || "",
    tare_wt: row.tare_wt || "",
    current_location: (row.current_location || "").toUpperCase(),
    current_depot_id: row.current_location === "Depot" ? row.location_id : "",
    current_plant_id: row.current_location === "Plant" ? row.location_id : "",
    current_customer_id: row.current_location === "Customer" ? row.location_id : "",
    customer_id: row.owner === "CUSTOMER" && row.current_location === "Customer" ? row.location_id : "",
    status: "Empty",
    created_by: String(userInfo.user_id),
  };
    const formData = new FormData();
    formData.append("cylinderData", JSON.stringify(cylinderData));
    return formData;
  };

  const handleSaveAll = async () => {
    if (rows.length === 0) {
      showError("Please add at least one cylinder to the table");
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const row of rows) {
      try {
        const payload = buildCylinderPayload(row);
        await cylinderMasterService.addCylinder(payload);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Error saving cylinder ${row.cylinder_no}:`, error);
      }
    }

    if (failCount === 0) {
      showSuccess(`${successCount} cylinder(s) added successfully`);
      setRows([]);
      setTimeout(() => navigate(-1), 1200);
    } else {
      showError(`${successCount} added, ${failCount} failed. Check console for details.`);
    }
  };

  return (
    <Box sx={pageSx}>

      {/* Breadcrumb */}
      <Box sx={breadcrumbSx}>
        <span>Dashboard</span> / <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Cylinder Location Entry</span>
      </Box>

      {/* Page Header */}
      <Box sx={pageHeaderSx}>
        <Box sx={pageTitleSx}>
          <LocationOnIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
          CYLINDER LOCATION ENTRY
        </Box>
        <Button onClick={() => navigate(-1)} sx={backBtnSx}>
          <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
        </Button>
      </Box>

      {/* ── Section: Cylinder Details Form ── */}
      <Box sx={formCardSx}>
        <Box sx={sectionHeaderSx}>
          <LocationOnIcon sx={{ fontSize: 18, color: "#fff" }} />
          <Typography sx={sectionHeaderTitleSx}>Cylinder Details</Typography>
        </Box>

        <Box sx={formBodySx}>
          <Grid container spacing={2}>

            {/* Cylinder No */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={fieldLabelSx}>Cylinder No <span style={{ color: "#E53E3E" }}>*</span></Typography>
              <TextField
                size="small" fullWidth value={form.cylinder_no} sx={inputSx}
                onChange={(e) => handleChange("cylinder_no", e.target.value)}
                placeholder="Enter cylinder no"
              />
            </Grid>

            {/* Tag No */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={fieldLabelSx}>Tag No <span style={{ color: "#E53E3E" }}>*</span></Typography>
              <TextField
                size="small" fullWidth value={form.tag_no} sx={inputSx}
                onChange={(e) => handleChange("tag_no", e.target.value)}
                placeholder="Enter tag no"
              />
            </Grid>

            {/* Owner */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={fieldLabelSx}>Owner</Typography>
              <TextField
                select size="small" fullWidth value={form.owner} sx={inputSx}
                onChange={(e) => handleChange("owner", e.target.value)}
              >
                <MenuItem value="TGPL" sx={menuItemSx}>TGPL</MenuItem>
                <MenuItem value="CUSTOMER" sx={menuItemSx}>CUSTOMER</MenuItem>
              </TextField>
            </Grid>

            {/* Product Code */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={fieldLabelSx}>Product Code</Typography>
              <Autocomplete
                disablePortal
                options={product || []}
                getOptionLabel={(option) => option.prod_code || ""}
                value={product.find((p) => p.prod_code === form.prod_code) || null}
                onChange={handleProductChange}
                isOptionEqualToValue={(option, value) => option.prod_id === value.prod_id}
                renderOption={(props, option) => (
                  <MenuItem {...props} sx={menuItemSx}>{option.prod_code}</MenuItem>
                )}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" fullWidth sx={inputSx} />
                )}
              />
            </Grid>

            {/* Tare Weight */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={fieldLabelSx}>Tare Weight</Typography>
              <TextField
                size="small" fullWidth type="number" value={form.tare_wt} sx={inputSx}
                onChange={(e) => handleChange("tare_wt", e.target.value)}
                placeholder="Enter tare weight"
              />
            </Grid>

            {/* Current Location Type */}
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography sx={fieldLabelSx}>Current Location <span style={{ color: "#E53E3E" }}>*</span></Typography>
              <TextField
                select size="small" fullWidth value={form.current_location} sx={inputSx}
                onChange={(e) => handleLocationTypeChange(e.target.value)}
              >
                {/* <MenuItem value=""><em>Select</em></MenuItem> */}
                <MenuItem value="Depot" sx={menuItemSx}>DEPOT</MenuItem>
                <MenuItem value="Plant" sx={menuItemSx}>PLANT</MenuItem>
                <MenuItem value="Customer" sx={menuItemSx}>CUSTOMER</MenuItem>
              </TextField>
            </Grid>

            {/* Location Value (conditional) */}
            {form.current_location && (
              <Grid item xs={12} sm={6} md={2.4}>
                <Typography sx={fieldLabelSx}>
                  Select {form.current_location} <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <Autocomplete
                  disablePortal
                  options={getLocationOptions()}
                  getOptionLabel={getLocationOptionLabel}
                  value={form.location_option}
                  onChange={(event, newValue) => handleChange("location_option", newValue)}
                  isOptionEqualToValue={(option, value) =>
                    getLocationOptionLabel(option) === getLocationOptionLabel(value)
                  }
                  renderOption={(props, option) => (
                    <MenuItem {...props} sx={menuItemSx}>{getLocationOptionLabel(option)}</MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" fullWidth sx={inputSx} />
                  )}
                    componentsProps={{
    popper: {
      placement: "bottom-start",
      modifiers: [
        { name: "flip", enabled: false },
        { name: "preventOverflow", enabled: false },
      ],
    },
  }}
                />
              </Grid>
            )}

          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={handleAddToTable} sx={addBtnSx}>
              <AddIcon sx={{ fontSize: 18 }} />
              Add to List
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Section: Table ── */}
      <Box sx={formCardSx}>
        <TableContainer sx={{ boxShadow: "none" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <StyledTableRow>
                {["Sr No", "Cylinder No", "Tag No", "Owner", "Product Code", "Current Location", "Tare Wt", "Action"].map((h, i) => (
                  <StyledTableCell key={i} align={i === 0 ? "center" : "left"} sx={tableHeadCellSx}>{h}</StyledTableCell>
                ))}
              </StyledTableRow>
            </TableHead>
            <TableBody sx={{ backgroundColor: "#fff" }}>
              {rows.length > 0 ? (
                rows.map((row, index) => (
                  <StyledTableRow key={index} sx={{ background: index % 2 === 0 ? "#fff" : "#F8FAFC" }}>
                    <StyledTableCell align="center" sx={{ fontSize: "12px" }}>{row.sr_no}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "12px" }}>{row.cylinder_no}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "12px" }}>{row.tag_no}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "12px" }}>{row.owner}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "12px" }}>{row.prod_code || "—"}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "12px" }}>{row.current_location} - {row.location_label}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: "12px" }}>{row.tare_wt || "—"}</StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton size="small" onClick={() => handleRemoveRow(index)}>
                        <DeleteForeverIcon sx={{ fontSize: "18px", color: "#E53E3E" }} />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={8} align="center" sx={{ color: "#718096", py: 3, fontSize: "12px" }}>
                    No cylinders added yet — fill the form above and click "Add to List"
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={formFooterSx}>
          <Button sx={cancelBtnSx} onClick={() => navigate(-1)}>Cancel</Button>
          <Button sx={saveBtnSx} onClick={handleSaveAll}>
            <SaveIcon sx={{ fontSize: 18 }} />
            Save All ({rows.length})
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.severity}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default CylinderLocationEntry;